import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { CONTACT_EMAIL } from "@/lib/api";

const SECTIONS = [
    {
        title: "What we collect",
        body: "When you place an order we collect your name, email address, and shipping address — only what is needed to pack and ship your order and email you about it. Payments are handled entirely by Stripe; we never see or store your card number.",
    },
    {
        title: "Cookies on your device",
        body: "Your shopping bag is stored on your own device so it survives a closed tab — that is strictly necessary for the shop to work. With your permission (the banner's \"Accept\"), we also keep an anonymous visitor ID to count visits and see which pieces get viewed. Choosing \"Essential only\" means no visit stats are collected at all.",
    },
    {
        title: "What we never do",
        body: "No advertising trackers, no data brokers, no selling or renting your information. Your details are used for your order and nothing else.",
    },
    {
        title: "Your rights",
        body: `Wherever you live — including the EU/UK — you may ask to see, correct, export, or delete your personal data, and you may withdraw cookie consent anytime by clearing your browser storage. Email ${CONTACT_EMAIL} and we will take care of it, usually within a few days.`,
    },
    {
        title: "Where data lives",
        body: "Order and contact details are stored on our secure hosting in the United States. Shipping labels are created through Shippo (they receive your delivery address), and payment processing through Stripe — both under their own strict data-protection terms.",
    },
];

export default function PrivacyPage() {
    return (
        <main data-testid="privacy-page" className="mx-auto max-w-4xl px-6 py-24 lg:px-10">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-clay">Your privacy</p>
                <h1 className="mt-6 font-serif text-5xl font-light tracking-tighter text-gold md:text-6xl">
                    Simple, honest <span className="italic text-espresso">privacy.</span>
                </h1>
                <p className="mt-6 max-w-xl text-base leading-relaxed text-clay">
                    A small shop deserves plain words. This is everything about how Kichazone handles your
                    information — no legal fog.
                </p>
            </motion.div>
            <div className="mt-16 flex flex-col gap-px bg-line">
                {SECTIONS.map((s, i) => (
                    <motion.section
                        key={s.title}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: i * 0.05 }}
                        data-testid={`privacy-section-${i}`}
                        className="bg-sand py-10"
                    >
                        <h2 className="font-serif text-2xl font-medium tracking-tight">{s.title}</h2>
                        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-clay">{s.body}</p>
                    </motion.section>
                ))}
            </div>
            <div className="mt-14 flex flex-col gap-3 sm:flex-row">
                <a
                    href={`mailto:${CONTACT_EMAIL}?subject=Privacy%20request`}
                    data-testid="privacy-contact-button"
                    className="inline-flex items-center justify-center gap-3 bg-espresso px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-sand transition-colors duration-300 hover:bg-gold hover:text-espresso"
                >
                    Ask about your data
                </a>
                <Link
                    to="/returns"
                    data-testid="privacy-returns-link"
                    className="group inline-flex items-center justify-center gap-3 border border-espresso px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] transition-colors duration-300 hover:bg-espresso hover:text-sand"
                >
                    Return policy
                    <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
                <button
                    onClick={() => {
                        localStorage.removeItem("yt_consent");
                        localStorage.removeItem("yt_visitor_id");
                        window.location.href = "/";
                    }}
                    data-testid="privacy-cookie-reset"
                    className="inline-flex items-center justify-center gap-3 border border-line px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-clay transition-colors duration-300 hover:border-espresso hover:text-espresso"
                >
                    Reset cookie choices
                </button>
            </div>
        </main>
    );
}
